// ABC text helpers: comment stripping and the text-string escapes ABC 2.1
// allows in titles, annotations and lyrics (\'e = é, \ss = ß, &eacute;, é).

const LIGATURES: Record<string, string> = {
  ss: 'ß',
  AE: 'Æ',
  ae: 'æ',
  OE: 'Œ',
  oe: 'œ',
  AA: 'Å',
  aa: 'å',
  OO: 'Ø',
  oo: 'ø',
};

/** ABC 2.1's accent mnemonics, each a combining mark on the letter after it. */
const MARKS: Record<string, string> = {
  '`': '̀',
  "'": '́',
  '^': '̂',
  '~': '̃',
  '"': '̈',
  c: '̧',
  u: '̆',
  v: '̌',
  H: '̋',
  '=': '̄',
  '.': '̇',
};

const ENTITY_MARKS: Record<string, string> = {
  grave: '̀',
  acute: '́',
  circ: '̂',
  tilde: '̃',
  uml: '̈',
  cedil: '̧',
  ring: '̊',
};

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  szlig: 'ß',
  aelig: 'æ',
  AElig: 'Æ',
  oelig: 'œ',
  OElig: 'Œ',
  oslash: 'ø',
  Oslash: 'Ø',
  copy: '©',
  hellip: '…',
  ndash: '–',
  mdash: '—',
};

/** Decode ABC's text-string escapes into plain Unicode. */
export function decodeAbcText(text: string): string {
  let out = text
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/\\U([0-9a-fA-F]{8})/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/\\(ss|AE|ae|OE|oe|AA|aa|OO|oo)/g, (_, key: string) => LIGATURES[key])
    .replace(/\\([`'^~"cuvH=.])([A-Za-z])/g, (_, mark: string, letter: string) =>
      `${letter}${MARKS[mark]}`.normalize('NFC'),
    )
    .replace(/&(#\d+|#x[0-9a-fA-F]+|[A-Za-z]+);/g, (whole, name: string) => {
      if (name.startsWith('#x')) return String.fromCodePoint(parseInt(name.slice(2), 16));
      if (name.startsWith('#')) return String.fromCodePoint(parseInt(name.slice(1), 10));
      if (ENTITIES[name]) return ENTITIES[name];
      const accented = /^([A-Za-z])(grave|acute|circ|tilde|uml|cedil|ring)$/.exec(name);
      return accented ? `${accented[1]}${ENTITY_MARKS[accented[2]]}`.normalize('NFC') : whole;
    });
  out = out.replace(/\\([%&\\"])/g, '$1');
  return out;
}

/**
 * Remove a `%` comment from one line. A `%` inside a quoted string is text,
 * and `\%` is a literal percent sign.
 */
export function stripComment(line: string): string {
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '\\') {
      i++;
      continue;
    }
    if (c === '"') quoted = !quoted;
    else if (c === '%' && !quoted) return line.slice(0, i);
  }
  return line;
}

/** Fake-book titles file the article last ("Kesh, The"); read it first. */
export function normalizeAbcTitle(title: string): string {
  return title.replace(/^(.*), (A|An|The)$/, '$2 $1').trim();
}
